import { Injectable } from "@angular/core";
import { BehaviorSubject, Observable, of, tap } from "rxjs";
import { catchError } from "rxjs/operators";
import { HttpService } from '../../../core/shared/http.service';
import { JwtTokenService } from '../../../core/shared/jwt-token.service';
import { LoginData } from "../interfaces/login-data.interface";
import { UserDataResponse } from "../interfaces/login-data-response.interface";
import { ResetPasswordRequest, ValidateResetTokenRequest } from "../interfaces/reset-password.interface";
import {
  AuthenticatedLoginResponse,
  CompleteRequiredTwoFactorSetupRequest,
  EnableAuthenticatorRequest,
  EnableAuthenticatorResponse,
  LoginResponse,
  TwoFactorChallengeResponse,
  TwoFactorSetupChallengeResponse,
  TwoFactorVerifyRequest
} from '../interfaces/two-factor.interface';
import { AuthEventService } from './auth-event.service';
import { SessionService } from './session.service';
import { AuthTokenService } from 'src/app/core/shared/auth-token.service';

@Injectable({
  providedIn: 'root'
})
export class LoginService {
  private currentUser$ = new BehaviorSubject<UserDataResponse | null>(null);

  constructor(
    private httpService: HttpService,
    private jwtTokenService: JwtTokenService,
    private authEventService: AuthEventService,
    private sessionService: SessionService,
    private authTokenService: AuthTokenService
  ) {
  }

  public login(loginData: LoginData): Observable<LoginResponse> {
    return this.httpService.postWithCredentials<LoginResponse>(
      'User/authenticate',
      loginData,
      true
    ).pipe(
      tap(response => {
        if (this.isTwoFactorChallenge(response) || this.isTwoFactorSetupChallenge(response)) {
          return;
        }

        if (this.isAuthenticatedLoginResponse(response)) {
          this.completeAuthentication(response);
        }
      })
    );
  }

  public verifyTwoFactor(request: TwoFactorVerifyRequest): Observable<UserDataResponse> {
    return this.httpService.postWithCredentials<AuthenticatedLoginResponse>(
      'User/logintwostep',
      request,
      true
    ).pipe(
      tap(response => {
        this.completeAuthentication(response);
      })
    );
  }

  public getRequiredTwoFactorSetup(twoFactorSetupToken: string): Observable<EnableAuthenticatorResponse> {
    return this.httpService.post<EnableAuthenticatorResponse>(
      'User/required-two-factor-setup',
      { twoFactorSetupToken },
      false
    );
  }

  public completeRequiredTwoFactorSetup(request: CompleteRequiredTwoFactorSetupRequest): Observable<UserDataResponse> {
    return this.httpService.postWithCredentials<AuthenticatedLoginResponse>(
      'User/complete-required-two-factor-setup',
      request,
      true
    ).pipe(
      tap(response => {
        this.completeAuthentication(response);
      })
    );
  }

  public getAuthenticatorSetup(): Observable<EnableAuthenticatorResponse> {
    return this.httpService.get<EnableAuthenticatorResponse>('User/enableauthenticator');
  }

  public enableAuthenticator(request: EnableAuthenticatorRequest): Observable<void> {
    return this.httpService.post<void>('User/EnableAuthenticator', request);
  }

  public disableAuthenticator(): Observable<void> {
    return this.httpService.post<void>('User/disableauthenticator', {});
  }

  public logout(): void {
    this.authEventService.emitEvent({
      type: 'logout',
      userData: null
    });
    this.sessionService.logout();
    this.jwtTokenService.removeJwtToken();
    this.currentUser$.next(null);
    this.authTokenService.logout('manual');
  }

  public getUserViaJwtToken(): Observable<UserDataResponse> {
    return this.httpService.get<UserDataResponse>('User/self').pipe(
      tap(userData => {
        this.currentUser$.next(userData);
        this.authEventService.emitEvent({
          type: 'user-loaded',
          userData: userData
        });
      })
    );
  }

  public isLoggedIn(): boolean {
    const token = this.jwtTokenService.getJwtToken();
    return !!token;
  }

  public isLogInValid(): boolean {
    let token = this.jwtTokenService.getJwtTokenIfValid();
    return !!token;
  }

  public getCurrentUser(): Observable<UserDataResponse | null> {
    return this.currentUser$.asObservable();
  }

  /**
   * Starts the password-reset flow. Bypasses the generic error toast of HttpService and maps the
   * API's 404 (unknown address) to success, so the login screen cannot reveal registered addresses.
   */
  public forgotPassword(email: string): Observable<void> {
    return this.httpService.postWithCredentials<void>('User/forgot-password', { email }, false).pipe(
      catchError(error => {
        if (error?.status === 404)
          return of(void 0);
        throw error;
      })
    );
  }

  /** Validates the reset token from the email link (initial password after user creation or forgot password). */
  public validateResetToken(token: string): Observable<void> {
    const request: ValidateResetTokenRequest = { token };
    return this.httpService.post<void>('User/validate-reset-token', request, false);
  }

  /**
   * Sets a new password with a valid reset token. The API answers like 'authenticate': the user is
   * logged in right away (JWT + refresh cookie) or gets the 2FA setup/challenge step. 422 if the token
   * is invalid or the password violates the policy (the latter is shown as a toast by HttpService).
   */
  public resetPassword(token: string, password: string): Observable<LoginResponse> {
    const request: ResetPasswordRequest = { token, password };
    return this.httpService.postWithCredentials<LoginResponse>('User/reset-password', request, true, true).pipe(
      tap(response => {
        if (this.isAuthenticatedLoginResponse(response)) {
          this.completeAuthentication(response);
        }
      })
    );
  }

  public getJwtToken(): string {
    return this.jwtTokenService.getJwtToken() ?? '';
  }

  public refreshAccessToken(): Observable<any> {
    return this.httpService.getWithCredentials<any>('User/refresh-token', true).pipe(
      tap(response => {
        this.jwtTokenService.setJwtToken(response.jwtToken);
      })
    );
  }

  private completeAuthentication(response: UserDataResponse): void {
    this.jwtTokenService.setAccessToken(response.jwtToken);
    this.currentUser$.next(response);
    this.sessionService.initializeSession();
    this.authEventService.emitEvent({
      type: 'login',
      userData: response
    });
  }

  public isTwoFactorChallenge(response: LoginResponse): response is TwoFactorChallengeResponse {
    return response.step === 'requires_two_factor';
  }

  public isTwoFactorSetupChallenge(response: LoginResponse): response is TwoFactorSetupChallengeResponse {
    return response.step === 'requires_two_factor_setup';
  }

  public isAuthenticatedLoginResponse(response: LoginResponse): response is AuthenticatedLoginResponse {
    return response.step === 'authenticated' && 'jwtToken' in response && !!response.jwtToken;
  }
}
